import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import Card from '@luna-protocol/core/src/components/Card/Card';
import CardRow from '@luna-protocol/core/src/components/CardRow/CardRow';
import Button from '@luna-protocol/core/src/components/Button/Button';
import { Form, Formik } from 'formik';
import { useQueryClient } from 'react-query';
import { formatDate, USDollar, NumberToPercentage } from '@luna-protocol/core/src/utils/constants/format';
import { useGetApplicationSummary } from '../../queries/useGetApplicationSummary';
import { DeliveryDateReq, usePostDeliveryDate } from '../../queries/usePostDeliveryDate';
import useDeliveryDateStatusCheck from '../../utils/hooks/useDeliveryDateStatusCheck.ts';
import { toastStore } from '@chakra-ui/react';
import useToast from '@luna-protocol/core/src/utils/useToast.ts';
import messages from './ApprovalDetails.messages';
import styles from './ApprovalDetails.module.scss';
import { SET_DELIVERY_DATE } from '../../queries/constants';

type ApprovalDetailsProps = {
  className?: string;
};

const ApprovalDetails = ({ className }: ApprovalDetailsProps) => {
  const { loan_application_id: loanApplicationID } = useParams();
  const { formatMessage } = useIntl();
  const { data, isLoading, refetch: refetchAppDetails } = useGetApplicationSummary(loanApplicationID);
  const [expandedStips, setExpandedStips] = useState<Record<number, boolean>>({});
  const [deliveryEditable, setDeliveryEditable] = useState(false);
  const [deliverySetEnable] = useState(false);
  const { postDeliveryDate } = usePostDeliveryDate();
  const { deliveryDateStatusCheck } = useDeliveryDateStatusCheck();
  const queryClient = useQueryClient();
  const { createToast } = useToast();
  const toastPro = toastStore;

  const toggleStip = (idx: number) => {
    setExpandedStips(prev => ({
      ...prev,
      [idx]: !(prev[idx] ?? !data?.stipulations?.[idx]?.cleared),
    }));
  };

  const incomingStips = data?.stipulations && data.stipulations.length > 0;

  const onSubmit = (values: DeliveryDateReq) => {
    values.loan_application_id = loanApplicationID ?? '';
    postDeliveryDate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries(SET_DELIVERY_DATE);
        refetchAppDetails();
        setDeliveryEditable(!deliveryEditable);
      },
      onError: () => {
        if (!toastPro.isActive('set-delivery-date-error')) {
          createToast({
            title: formatMessage(messages.deliveryDateError),
            status: 'error',
            isClosable: true,
            duration: null,
            id: 'set-delivery-date-error',
          });
        }
      },
    });
  };

  return (
    <div className={className}>
      <p className={styles.approvalDetails__title}>{formatMessage(messages.title)}</p>
      <Card>
        <CardRow label={formatMessage(messages.decision)} value={data?.decision_status} />
        <CardRow label={formatMessage(messages.tier)} value={data?.risk_tier} />
        <CardRow label={formatMessage(messages.appNumber)} value={data?.afi_application_id} />
        <CardRow
          label={formatMessage(messages.prequalDate)}
          value={data?.prequal_date && formatDate(new Date(data?.prequal_date).toISOString())}
        />
        <CardRow
          label={formatMessage(messages.expirationDate)}
          value={data?.expiration_date && formatDate(new Date(data?.expiration_date).toISOString())}
        />
        <CardRow label={formatMessage(messages.maxRate)} value={data?.max_rate} />
        <CardRow label={formatMessage(messages.maxTerm)} value={data?.max_term} />
        <CardRow
          label={formatMessage(messages.amountRequested)}
          value={data?.amount_requested && USDollar.format(data?.amount_requested)}
        />
        <CardRow
          label={formatMessage(messages.actualLtv)}
          value={'-'}
          // value={data?.frontend_ltv != null ? NumberToPercentage(data?.frontend_ltv) : '-'}
        />
        <CardRow label={formatMessage(messages.maxLtv)} value={data?.max_ltv && NumberToPercentage(data?.max_ltv)} />
        <CardRow
          label={formatMessage(messages.maxBackend)}
          // value={data?.max_backend_overall && USDollar.format(data?.max_backend_overall)}
          value=""
        />
        <CardRow
          label={formatMessage(messages.minCashDown)}
          value={data?.down_payment && USDollar.format(data?.down_payment)}
        />
        {data && !isLoading && (
          <Formik
            initialValues={{
              loan_application_id: loanApplicationID ?? '',
              delivery_date: data.delivery_date ? data.delivery_date.split('T')[0] : '',
            }}
            onSubmit={onSubmit}
            enableReinitialize>
            {({ values, setFieldValue, touched, errors, resetForm }) => (
              <Form className={styles.approvaldetails__form}>
                <div className={styles.inlineRow}>
                  <CardRow
                    label={formatMessage(messages.deliveryDate)}
                    value={values.delivery_date !== '0001-01-01' ? values.delivery_date : ''}
                    isEditMode={deliveryEditable}
                    inputType="date"
                    handleChange={event => setFieldValue('delivery_date', event.target.value)}
                    touched={touched.delivery_date}
                    error={errors.delivery_date}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                  {deliverySetEnable &&
                    (deliveryEditable ? (
                      <>
                        <Button
                          className={styles.applicantDetails__button}
                          type="submit"
                          variant="secondary"
                          fullWidth={false}>
                          Save
                        </Button>
                        <Button
                          className={styles.applicantDetails__button}
                          type="button"
                          variant="secondary"
                          fullWidth={false}
                          onClick={event => {
                            event.preventDefault();
                            resetForm();
                            setDeliveryEditable(!deliveryEditable);
                          }}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        className={styles.applicantDetails__button}
                        type="button"
                        variant="secondary"
                        fullWidth={false}
                        disabled={deliveryDateStatusCheck(data.status)}
                        onClick={event => {
                          event.preventDefault();
                          setDeliveryEditable(!deliveryEditable);
                        }}>
                        Edit
                      </Button>
                    ))}
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Card>
      {incomingStips && (
        <Card>
          <p className={styles.approvalDetails_title}>{formatMessage(messages.stipulations)}</p>

          {incomingStips ? (
            data.stipulations.map((stip, idx) => {
              const isExpanded = expandedStips[idx] ?? !stip.cleared;
              const bodyId = `stip-body-${idx}`;

              return (
                <div key={`${stip.subject ?? 'stip'}-${idx}`} className={styles.stipRow}>
                  <div
                    className={`${styles.stipRow_group} ${
                      stip.cleared ? styles.stipRow_groupCleared : styles.stipRow_groupUncleared
                    }`}>
                    <button
                      type="button"
                      className={`${styles.stipRow_header} ${
                        stip.cleared ? styles.stipRow_headerCleared : styles.stipRow_headerUncleared
                      }`}
                      onClick={() => toggleStip(idx)}
                      aria-expanded={isExpanded}
                      aria-controls={bodyId}>
                      <span className={styles.stipRow_headerText} title={stip.short_text}>
                        {stip.short_text && <span className={styles.stipRow_shortText}>{stip.short_text}</span>}
                      </span>

                      <span className={styles.stipRow_headerIcon} aria-hidden="true">
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    </button>

                    {isExpanded && stip.long_text && (
                      <div id={bodyId} className={styles.stipRow_body}>
                        {stip.long_text}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p>{formatMessage(messages.noStips)}</p>
          )}
        </Card>
      )}
      <Card>
        <p className={styles.approvalDetails__title}>{formatMessage(messages.notes)}</p>
        {data?.decision_comments && data.decision_comments.length > 0 ? (
          data.decision_comments.map((comment, idx) => <CardRow key={comment + '-' + idx} value={comment} />)
        ) : (
          <p>{formatMessage(messages.noNotes)}</p>
        )}
      </Card>
    </div>
  );
};

export default ApprovalDetails;
